import { AppConstant, KeyConstant, SystemConstant } from "const";
import store, { ConversationActions } from "redux-store";
import { getInteractor } from "services/local.service";
import { StorageUtil, toSnake, uuid } from "utils";
import { validateInvolved, validateMentions } from "utils/view.utils";

export const updateThread = async (prefixKey, newMessage) => {
  if (!prefixKey) prefixKey = StorageUtil.getCurrentPrefixKey();

  const message = await getInteractor(prefixKey).LocalMessageService.get(newMessage.id);
  if (false === Boolean(message && message.id)) return false;
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const deviceId = StorageUtil.getItem(KeyConstant.KEY_DEVICE_ID, prefixKey);

  if (
    SystemConstant.IGNORE_MESSAGE.includes(message.send_type) ||
    false === Boolean(message.thread_id || message.parent_id)
  )
    return false;

  const threadId = message.thread_id || message.parent_id;

  // Checking parent message of reply message is deleted only me
  const isDeleteOnlyMe = message.send_type === SystemConstant.SEND_TYPE.deleteMessageOnlyMe;
  const existedThread = await getInteractor(prefixKey).LocalThreadService.findOne({
    thread_id: message.parent_id,
  });
  const isDeleteOnlyMeParentMessage = Boolean(existedThread && existedThread.id && isDeleteOnlyMe);

  if (false === Boolean(message.thread_id) && false === isDeleteOnlyMeParentMessage) return false;

  try {
    if (isDeleteOnlyMeParentMessage) {
      // Delete only me parent message => update state = inactive, total_reply = 0, total_unread = 0
      await getInteractor(prefixKey).LocalThreadService.update(
        {
          state: SystemConstant.STATE.inactive,
          total_reply: 0,
          total_unread: 0,
        },
        { thread_id: threadId },
      );

      return true;
    }

    const threadInfo = await getInteractor(prefixKey).LocalThreadService.getByThreadId(threadId);
    let parentMessage = await getInteractor(prefixKey).LocalMessageService.findOne({
      source_id: threadId,
    });

    // If parent message is not existed in local db and reply message is original
    // => create new record with send_type = notFound, source_id = thread_id
    if (false === Boolean(parentMessage && parentMessage.id) && false === Boolean(message.parent_id)) {
      parentMessage = {
        account_id: accountId,
        branch_id: message.branch_id,
        content: "",
        created: message.created - 1,
        device_id: deviceId,
        group_id: message.group_id,
        id: uuid(),
        mentions: "[]",
        modified: message.created - 1,
        options: "",
        parent_id: null,
        send_type: SystemConstant.SEND_TYPE.notFound,
        sender_device_id: "",
        sender_id: "",
        source_id: message.thread_id,
        state: 1,
        status: SystemConstant.MESSAGE_STATUS.read,
        thread_id: null,
        call_status: null,
        room_id: null,
      };

      await getInteractor(prefixKey).LocalMessageService.save([parentMessage]);

      store.dispatch(ConversationActions.receivedRemoteMessage(parentMessage));
    }

    if (parentMessage && parentMessage.id) {
      const totalReply = await getInteractor(prefixKey).LocalMessageService.countTotalReplyInThread(threadId);
      const totalUnread = await getInteractor(prefixKey).LocalMessageService.countUnreadMessageInThread(
        accountId,
        threadId,
      );
      const threadMentions = validateMentions(threadInfo, message.mentions, parentMessage.mentions);
      const isInvolved = validateInvolved(message, parentMessage, threadMentions)
        ? SystemConstant.STATE.active
        : SystemConstant.STATE.inactive;
      const modifiedThread = Boolean(message.parent_id)
        ? threadInfo?.modified
        : Math.max(threadInfo?.modified, message.created, 0);
      const accountReplyList = await getInteractor(prefixKey).LocalMessageService.getAccountReply(threadId);

      if (threadInfo && threadInfo.id) {
        // thread is existed and message is in thread => update thread record: state, total_reply, total_unread, modified
        const updateThreadInfo = {
          totalReply: totalReply,
          totalUnread: totalUnread,
          involvedF: threadInfo.involved_f || isInvolved,
          state: totalReply > 0 ? AppConstant.THREAD_STATE.active : AppConstant.THREAD_STATE.inactive,
          modified: modifiedThread,
          threadMentions: JSON.stringify(threadMentions),
          settingType: threadInfo.setting_type,
          accountReply: JSON.stringify(accountReplyList || []),
        };

        await getInteractor(prefixKey).LocalThreadService.update(toSnake(updateThreadInfo), { thread_id: threadId });
      } else {
        // thread is not existed and message is in thread => create new thread record
        const createdTime = Date.now();

        const newThreadInfo = {
          id: uuid(),
          threadId: threadId,
          groupId: message.group_id,
          totalReply: totalReply,
          totalUnread: totalUnread,
          involvedF: isInvolved,
          state: AppConstant.THREAD_STATE.active,
          created: createdTime,
          modified: createdTime,
          threadAccountId: parentMessage.sender_id,
          threadCreated: parentMessage.created,
          threadContent: parentMessage.content,
          threadMentions: JSON.stringify(threadMentions),
          branchId: message.branch_id,
          groupType: null,
          settingType: null,
          force_sync_f: 1,
          accountReply: JSON.stringify(accountReplyList || []),
        };

        await getInteractor(prefixKey).LocalThreadService.save([toSnake(newThreadInfo)]);
      }

      return Boolean(isInvolved || threadInfo?.involved_f);
    }
  } catch (error) {
    console.error(error);
  }

  return false;
};
